import React from 'react';
import { actions } from 'xstate';
import pages from './pages';
import { navigate } from '@reach/router';
import UserContext from '../UserContext';
import get from 'lodash/get';
import Splasher from '../../components/Splasher';
import useMachine from '../../hooks/useMachine';
import useCurrentWeek from '../../hooks/useCurrentWeek';
import useMyPpr from '../../hooks/useMyPpr';
import ShopTalkDrawer from './ShopTalkDrawer';

const { assign } = actions;

export const DashboardContext = React.createContext();
const machineConfig = {
  id: 'dashboardContext',
  context: {
    currentPage: pages[0].key,
    pages
  },
  initial: 'initializing',
  states: {
    initializing: {
      on: {
        DONE: {
          target: 'showContent',
          actions: 'initContent'
        }
      }
    },
    showContent: {
      on: {
        CHANGE_PAGE: {
          actions: 'changePage'
        },
        HIDE_PERIOD_SELECTOR: {
          actions: 'hidePeriodSelector'
        },
        SHOW_PERIOD_SELECTOR: {
          actions: 'showPeriodSelector'
        },
        CHANGE_STORE: {
          actions: 'changeStore'
        },
        CHANGE_PERIOD: {
          actions: 'changePeriod'
        },
        DEACTIVATE: {
          target: 'showScreenSaver'
        },
        LOG_OUT: {
          target: 'loggedOut'
        }
      }
    },
    showScreenSaver: {
      on: {
        ACTIVATE: {
          target: 'showContent'
        }
      }
    },
    loggedOut: {
      type: 'final'
    }
  },
  onDone: {
    actions: 'onAuthentication'
  }
};

const machineOptions = {
  actions: {
    initContent: assign((ctx, evt) => {
      console.log('--- INIT DONE', evt.value);
      return {
        ...ctx,
        ...evt.value
      };
    }),
    changePeriod: assign((ctx, evt) => {
      if (window.history.pushState) {
        const params = new URLSearchParams(window.location.search);
        params.set('p', get(evt, 'value.dateFrom'));
        const newurl =
          window.location.protocol +
          '//' +
          window.location.host +
          window.location.pathname +
          '?' +
          params;
        window.history.pushState({ path: newurl }, '', newurl);
      }
      return {
        currentPeriod: evt.value
      };
    }),
    hidePeriodSelector: assign(() => ({ showPeriodSelector: false })),
    showPeriodSelector: assign(() => ({ showPeriodSelector: true })),
    changePage: assign((ctx, evt) => {
      const page = ctx.pages.find(p => p.key === evt.value);
      //navigate to page route
      page.path && navigate(page.path);
      return {
        currentPage: evt.value
      };
    }),
    changeStore: assign((ctx, evt) => {
      // console.log('evt'); //TRACE
      // console.log(evt); //TRACE
      return {
        currentStore: evt.value.storeId,
        currentStoreDetails: evt.value.store
      };
    })
  },
  guards: {},
  services: {}
};

function MachineBody({ content, machine }) {
  const { currentUser } = React.useContext(UserContext);
  const [ppr, setPpr] = React.useState(null);
  const [shopTalkState, setShopTalkState] = React.useState();

  const selectedPeriod = get(machine, 'state.context.currentPeriod');
  const selectedPeriodDate = React.useMemo(() => {
    if (selectedPeriod) {
      return new Date(get(selectedPeriod, 'dateFrom'));
    } else {
      const urlParams = new URLSearchParams(window.location.search);
      const p = urlParams.get('p');
      if (p) {
        return new Date(p);
      } else {
        return new Date();
      }
    }
  }, [selectedPeriod]);
  console.log('>>DashboardContext/index::', 'selectedPeriodDate', selectedPeriodDate); //TRACE
  const {
    period, //initial week
    previousPeriod,
    nextPeriod,
    loading: periodLoading
  } = useCurrentWeek({ dateNow: selectedPeriodDate });
  console.log(
    '>>DashboardContext/index::',
    'periodData,periodLoading',
    periodLoading,
    period
  ); //TRACE
  const memberId = get(currentUser, 'memberInfo.id');
  const periodId = get(period, 'id');
  // const { data: userPprData, loading: userPprLoading } = useQuery(getMemberPpr().query, {
  //   skip: !shouldFetchPpr || !memberId || !periodId,
  //   fetchPolicy: 'network-only',
  //   variables: { memberId, periodId },
  // });
  const { data: userPprData, loading: userPprLoading } = useMyPpr({
    memberId,
    periodId
  });
  console.log('>>DashboardContext/index::', 'userPprData', userPprData); //TRACE

  React.useEffect(() => {
    if (!userPprLoading && userPprData) {
      setPpr(userPprData);
    }
  }, [userPprData, userPprLoading]);

  const { service } = machine;
  React.useEffect(() => {
    const storeId = get(currentUser, 'memberInfo.store.id');
    const store = get(currentUser, 'memberInfo.store');
    const userRoleGroup = get(currentUser, 'memberInfo.role.group');
    const ctxVal = {};
    if (userRoleGroup !== 'Admins') {
      ctxVal.currentStore = storeId;
      ctxVal.currentStoreDetails = store;
    }
    service.send({ type: 'DONE', value: ctxVal });
  }, [currentUser, service]);

  const contextState = React.useMemo(() => {
    return {
      ...machine,
      currentUserPpr: { ...ppr, loading: userPprLoading },
      previousPeriod,
      getPreviousPeriod: () => {
        return previousPeriod;
      },
      nextPeriod,
      getNextPeriod: () => {
        return nextPeriod;
      },
      openShopTalk(args) {
        setShopTalkState(args);
      }
    };
  }, [machine, ppr, userPprLoading, previousPeriod, nextPeriod]);

  React.useEffect(() => {
    if (period) {
      machine.service.send({ type: 'CHANGE_PERIOD', value: period });
    }
  }, [period, machine.service]);

  return (
    <DashboardContext.Provider value={contextState}>
      {machine.state.matches('initializing') ? <Splasher /> : content}
      <ShopTalkDrawer
        open={!!shopTalkState}
        {...(shopTalkState || {})}
        onClose={() => setShopTalkState(null)}
      />
    </DashboardContext.Provider>
  );
}

export function Provider(props) {
  const { service, state } = useMachine({ machineConfig, machineOptions });
  return <MachineBody content={props.children} machine={{ service, state }} />;
}

export default DashboardContext;
